/**
 * UserInteractionDrawer - the single renderer for every mid-run `awaiting_input`
 * prompt (ask_user / requestUserInput), mounted above the chat composer.
 *
 * One protocol, one control (CLAUDE.md "Reusable primitives"): confirm and
 * choice prompts render as option rows (with descriptions, number-key/arrow
 * selection), text prompts as an input with suggestion chips, file prompts as a
 * drag-and-drop upload zone (assetKind → endpoint via promptUploadTargets), and
 * kind 'form' as a tabbed multi-question panel. An optional `link` renders as an
 * external button (OAuth consent flows). When `allowFreeText` is set, a free-
 * text input is always available under the options.
 *
 * Escape never cancels the run (Stop in the composer is the cancel path) - it
 * just returns focus to the composer.
 */

import type {
  UserPromptAnswer,
  UserPromptChoice,
  UserPromptFileSpec,
  UserPromptKind,
  UserPromptLink,
  UserPromptQuestion,
  UserPromptResponse,
} from '@protoss/shared';
import { ChevronLeftIcon, ChevronRightIcon, IconSize, Pill, Tabs } from '@protoss/ui';
import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';
import { openFilePicker } from '../../../lib/googlePicker';
import { useGoogleDriveIntegration } from '../../../hooks/useGoogleDriveIntegration';
import { useProtectedImageUrl } from '../../../hooks/useProtectedImageUrl';
import {
  assetsFromDrive,
  driveMimeTypes,
  supportsDriveImport,
  uploadErrorMessage,
  uploadPromptFile,
  type DrivePickedFile,
  type UploadedPromptAsset,
} from '../lib/promptUploadTargets';
import type { AssistantRun } from '../state/chatState';
import { DocSectionView } from './DocSectionView';
import { FilePreviewDialog } from './FilePreviewDialog';

/** Synthetic option id for the generic "Other - type your own answer" affordance. */
const OTHER_OPTION_ID = '__other__';

/** True when a choice's optional profile has any content worth an expandable panel. */
function hasProfileContent(profile: UserPromptChoice['profile']): boolean {
  return (
    !!profile &&
    Boolean(
      profile.summary ||
      profile.facts?.length ||
      profile.components?.length ||
      profile.flowNotes?.length
    )
  );
}

type PendingPrompt = NonNullable<AssistantRun['pendingPrompt']>;

export interface UserInteractionDrawerProps {
  prompt: PendingPrompt;
  onRespond: (promptId: string, response: UserPromptResponse) => void;
  disabled?: boolean;
}

/** Sub-answer for one question (form mode) or the whole prompt (single mode). */
type QuestionAnswer = Omit<UserPromptAnswer, 'questionId'>;

/** Structural question shape - satisfied by both the prompt and form questions. */
interface QuestionLike {
  kind: UserPromptKind;
  prompt: string;
  details?: string;
  /** Short tab caption (form mode) - used as the inline preview's file name. */
  header?: string;
  choices?: UserPromptChoice[];
  suggestions?: string[];
  allowFreeText?: boolean;
  /** kind 'text': may be left empty - the drawer shows a "Skip" button. */
  optional?: boolean;
  /** Suppress the default "Other" free-text affordance for a closed choice set. */
  closedChoices?: boolean;
  /** kind 'choice': render as a checkbox multi-select with a Confirm action. */
  multiSelect?: boolean;
  /** Multi-select: minimum options to check before Confirm enables (default 1; 0 ⇒ skippable). */
  minSelections?: number;
  /** Multi-select: maximum options accepted (absent ⇒ unbounded). */
  maxSelections?: number;
  file?: UserPromptFileSpec;
  link?: UserPromptLink;
  /** Monospace content panel rendered above the options (form-tab "actual data"). */
  preview?: string;
  /** kind 'choice': option id to mark "(recommended)" and float to the top. */
  recommendedChoiceId?: string;
  /** Asset rendered inline (section-scoped) in the preview pane. */
  inlinePreviewAssetUuid?: string;
  /** Section start heading - the inline preview clips from here. */
  inlinePreviewHeading?: string;
  /** Next section's heading - the inline preview clips up to here. */
  inlinePreviewEndHeading?: string;
  /** Read-only pills shown as a row above the body (display only, e.g. detected tokens). */
  chips?: Array<{ label: string }>;
}

function focusComposer(): void {
  const el = document.querySelector<HTMLTextAreaElement>('.ai-chat-composer__textarea');
  el?.focus();
}

/**
 * Image preview with graceful degradation: a previewAssetUuid pointing at a
 * non-image asset makes the <img> error - fall back to the option's text preview.
 */
function PreviewImage({ src, fallback }: { src: string; fallback?: string }) {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return fallback ? <pre className="ai-interaction__preview-text">{fallback}</pre> : null;
  }
  return (
    <img
      className="ai-interaction__preview-image"
      src={src}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

// ── Single-question body (also used inside each form tab) ────────────────────

function QuestionBody({
  question,
  onAnswer,
  disabled,
  autoFocus,
  initialAnswer,
}: {
  question: QuestionLike;
  onAnswer: (answer: QuestionAnswer) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Form mode: the recorded/recommended answer that seeds selection on (re)mount. */
  initialAnswer?: QuestionAnswer;
}) {
  // Pre-selection (form mode): a recommended answer seeds the highlighted option and a
  // persistent "selected" affordance, restored when the user revisits the tab.
  const initialSelectedId =
    initialAnswer?.kind === 'choice'
      ? initialAnswer.choiceId
      : initialAnswer?.kind === 'confirm'
        ? initialAnswer.confirmed
          ? 'yes'
          : 'no'
        : undefined;
  const [text, setText] = React.useState(initialAnswer?.text ?? '');
  const [activeIndex, setActiveIndex] = React.useState(() => {
    const ids =
      question.kind === 'confirm' ? ['yes', 'no'] : (question.choices ?? []).map((c) => c.id);
    const idx = initialSelectedId ? ids.indexOf(initialSelectedId) : -1;
    return idx >= 0 ? idx : 0;
  });
  const [selectedId, setSelectedId] = React.useState<string | undefined>(initialSelectedId);
  // Multi-select (kind 'choice' with multiSelect): checkbox set + explicit Confirm.
  const isMulti = question.kind === 'choice' && !!question.multiSelect;
  const [checkedIds, setCheckedIds] = React.useState<Set<string>>(
    () => new Set(initialAnswer?.kind === 'choice' ? (initialAnswer.choiceIds ?? []) : [])
  );
  const [oauthError, setOauthError] = React.useState<string | null>(null);
  /** True once the user picks the generic "Other" option - reveals the free-text box. */
  const [otherSelected, setOtherSelected] = React.useState(
    initialAnswer?.kind === 'text' && !!initialAnswer.text
  );
  const textInputRef = React.useRef<HTMLTextAreaElement>(null);
  // Auto-grow the textarea to fit its content. CSS caps it at ~10 lines (max-height);
  // only then do we switch the vertical scrollbar on, so a single line never scrolls.
  const autoGrowTextArea = React.useCallback(() => {
    const el = textInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxH = parseFloat(getComputedStyle(el).maxHeight);
    const needed = el.scrollHeight;
    const capped = Number.isFinite(maxH) && needed > maxH;
    el.style.height = `${capped ? maxH : needed}px`;
    el.style.overflowY = capped ? 'auto' : 'hidden';
  }, []);
  // Resize on (re)mount and whenever the value changes (seeded answers, paste, typing).
  React.useLayoutEffect(() => {
    autoGrowTextArea();
  });
  /** The option's document being previewed in the modal, if any. */
  const [previewAsset, setPreviewAsset] = React.useState<{
    uuid: string;
    fileName?: string;
    scrollToHeading?: string;
  } | null>(null);
  const [uploadState, setUploadState] = React.useState<
    | { phase: 'idle' }
    | { phase: 'uploading' }
    | { phase: 'uploaded'; asset: UploadedPromptAsset }
    | { phase: 'error'; message: string }
  >({ phase: 'idle' });
  /** Multi-file prompts (file.maxFiles > 1) accumulate uploads here. */
  const [multiAssets, setMultiAssets] = React.useState<UploadedPromptAsset[]>([]);
  // Drive re-import dedup: when a picked file is already in the library, the existing asset is
  // reused (kept usable) and we surface an "import a fresh copy" override over the picked files.
  const [driveReused, setDriveReused] = React.useState<{ files: DrivePickedFile[] } | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // Only images get a thumbnail; documents (.docx/.pdf) fall back to their name
  // below - building an <img> for a non-image renders a broken image.
  const uploadedAsset = uploadState.phase === 'uploaded' ? uploadState.asset : null;
  const uploadedIsImage = uploadedAsset?.mimeType?.startsWith('image/') ?? false;
  const previewUrl = useProtectedImageUrl(
    uploadedIsImage ? (uploadedAsset?.contentUrl ?? null) : null
  );

  const choices = question.choices ?? [];
  const recommendedId = question.recommendedChoiceId;
  // Float the recommended choice to the top (it is marked "(recommended)" below); the
  // rest keep their order. Pure choice prompts only - confirm is always Yes/No.
  const orderedChoices =
    recommendedId && choices.some((c) => c.id === recommendedId)
      ? [
          ...choices.filter((c) => c.id === recommendedId),
          ...choices.filter((c) => c.id !== recommendedId),
        ]
      : choices;
  const baseOptionRows: UserPromptChoice[] =
    question.kind === 'confirm'
      ? [
          { id: 'yes', label: 'Yes' },
          { id: 'no', label: 'No' },
        ]
      : orderedChoices;
  const hasPreviews = baseOptionRows.some((o) => o.preview || o.previewAssetUuid);
  // A "pick one of these documents" choice (each option references a real file) is
  // a closed enumeration - never offer a free-text answer. Belt-and-suspenders with
  // the skill's playbook.
  const hasDocumentChoices = baseOptionRows.some((o) => o.documentAssetUuid);
  // Generic "Other" affordance (Claude Code style): ON BY DEFAULT for choice prompts;
  // opt-in for confirm (allowFreeText). Suppressed for document picks and for closed
  // sets - `closedChoices` is the canonical opt-out (a single ask_user prompt's legacy
  // `allowFreeText: false` is mapped to it by the tool, since a false never survives
  // the WS forward); a form tab's per-question `allowFreeText: false` is honored here
  // directly (it rides inside questions[] verbatim).
  const offerOther =
    question.kind !== 'file' &&
    !question.closedChoices &&
    !hasDocumentChoices &&
    !isMulti && // a checkbox group can't carry an ambiguous typed "Other"
    (question.kind === 'choice'
      ? question.allowFreeText !== false
      : question.kind === 'confirm' && question.allowFreeText === true);
  const optionRows: UserPromptChoice[] = offerOther
    ? [
        ...baseOptionRows,
        { id: OTHER_OPTION_ID, label: 'Other', description: 'Type your own answer' },
      ]
    : baseOptionRows;
  // Inline section-scoped document preview (replaces the monospace excerpt pane).
  const hasInlineDoc = !!question.inlinePreviewAssetUuid;
  // The preview pane is sticky - it always shows a preview, not just on hover:
  // seeded with the first option that has one, switching only when focus lands
  // on another option with its own preview (never blanking out in between).
  const firstPreviewIndex = optionRows.findIndex((o) => o.preview || o.previewAssetUuid);
  const [previewIndex, setPreviewIndex] = React.useState(firstPreviewIndex);
  React.useEffect(() => {
    const option = optionRows[activeIndex];
    if (option?.preview || option?.previewAssetUuid) setPreviewIndex(activeIndex);
    // optionRows is rebuilt each render from the same question - activeIndex is
    // the only meaningful trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);
  const previewOption = optionRows[previewIndex] ?? optionRows[firstPreviewIndex];
  // Asset previews (e.g. "keep the current logo") render the actual image; the
  // text preview is the fallback for non-image assets and sketches/doc excerpts.
  const previewImageUrl = useProtectedImageUrl(
    previewOption?.previewAssetUuid
      ? `/api/workspace-assets/${previewOption.previewAssetUuid}/content`
      : null
  );

  const maxSelections = question.maxSelections;
  const minSelections = isMulti ? (question.minSelections ?? 1) : 0;

  const answerOption = (id: string) => {
    if (disabled) return;
    // "Other" does not submit a choice - it reveals + focuses the free-text box.
    if (id === OTHER_OPTION_ID) {
      setOtherSelected(true);
      // Focus after the input mounts on the next paint.
      requestAnimationFrame(() => textInputRef.current?.focus());
      return;
    }
    // Multi-select: toggle membership (don't submit); respect maxSelections.
    if (isMulti) {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (maxSelections === undefined || next.size < maxSelections) {
          next.add(id);
        }
        return next;
      });
      return;
    }
    setOtherSelected(false);
    setSelectedId(id);
    if (question.kind === 'confirm') {
      onAnswer({ kind: 'confirm', confirmed: id === 'yes' });
    } else {
      onAnswer({ kind: 'choice', choiceId: id });
    }
  };

  // Multi-select: submit the checked set (choiceId = first, for single-select consumers).
  const confirmMulti = () => {
    if (disabled) return;
    const ids = [...checkedIds];
    if (ids.length < minSelections) return;
    onAnswer({ kind: 'choice', choiceId: ids[0], choiceIds: ids });
  };

  const sendText = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onAnswer({ kind: 'text', text: trimmed });
    setText('');
  };

  // Optional text questions can be left empty: submit a blank answer so a form tab
  // counts as confirmed (and a single prompt completes) without forcing input.
  const skipText = () => {
    if (disabled) return;
    onAnswer({ kind: 'text', text: '' });
    setText('');
  };

  const handleOptionsKeyDown = (e: React.KeyboardEvent) => {
    if (optionRows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, optionRows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Multi-select: Cmd/Ctrl+Enter confirms the checked set; plain Enter toggles.
      if (isMulti && (e.metaKey || e.ctrlKey)) {
        confirmMulti();
        return;
      }
      const option = optionRows[activeIndex];
      if (option) answerOption(option.id);
    } else if (/^[1-9]$/.test(e.key)) {
      const option = optionRows[Number(e.key) - 1];
      if (option) {
        e.preventDefault();
        answerOption(option.id);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      focusComposer();
    }
  };

  // [OAuth auto-detect] A confirm prompt whose link starts an OAuth flow
  // resolves itself: /oauth/result broadcasts on the `<provider>-oauth` channel
  // when the consent tab finishes, so the user never has to click Yes manually.
  // A failure broadcast surfaces inline instead - the user can retry the link.
  const onAnswerRef = React.useRef(onAnswer);
  onAnswerRef.current = onAnswer;
  const disabledRef = React.useRef(disabled);
  disabledRef.current = disabled;
  const oauthProvider = question.link?.oauthProvider;
  const isConfirmKind = question.kind === 'confirm';
  React.useEffect(() => {
    if (!oauthProvider || !isConfirmKind) return;
    if (typeof BroadcastChannel === 'undefined') return;
    const providerKey = oauthProvider.toUpperCase().replace(/-/g, '_');
    const channel = new BroadcastChannel(`${oauthProvider}-oauth`);
    channel.onmessage = (event: MessageEvent) => {
      const type = (event.data as { type?: string } | null)?.type;
      if (type === `${providerKey}_OAUTH_SUCCESS`) {
        if (!disabledRef.current) {
          onAnswerRef.current({ kind: 'confirm', confirmed: true });
        }
      } else if (type === `${providerKey}_OAUTH_ERROR`) {
        setOauthError(
          'The connection did not complete in the other tab. Use the link to try again.'
        );
      }
    };
    return () => channel.close();
  }, [oauthProvider, isConfirmKind]);

  const maxFiles = question.file?.maxFiles ?? 1;
  const minFiles = Math.min(question.file?.minFiles ?? 1, maxFiles);
  const isMultiFile = maxFiles > 1;

  // Google Drive import is offered as a second source for any file prompt whose asset kind
  // supports it (workspace-admin kinds) when a Drive credential is connected. The Picker iframe
  // is a transient grant modal; the persistent UI (the file list below) is ours.
  const driveCapableKind =
    question.kind === 'file' && !!question.file && supportsDriveImport(question.file.assetKind);
  const drive = useGoogleDriveIntegration();
  const driveImportEnabled = driveCapableKind && drive.connected && !!drive.integrationUuid;

  const handleDriveImport = async () => {
    const spec = question.file;
    if (!spec || disabled || !drive.integrationUuid) return;
    // Open the Picker first (no "uploading" spinner while it's up); a cancel leaves state as-is.
    let picked: { id: string; name: string; mimeType: string }[];
    try {
      const cfg = await apiFetch<{
        accessToken: string;
        apiKey: string | null;
        appId: string | null;
      }>(`/api/integration/${drive.integrationUuid}/picker-config`);
      if (!cfg.apiKey) {
        setUploadState({ phase: 'error', message: 'Google Drive picker is not configured.' });
        return;
      }
      picked = await openFilePicker({
        accessToken: cfg.accessToken,
        apiKey: cfg.apiKey,
        appId: cfg.appId,
        multi: isMultiFile,
        mimeTypes: driveMimeTypes(spec.assetKind, spec.accept),
      });
    } catch (err) {
      setUploadState({ phase: 'error', message: uploadErrorMessage(err) });
      return;
    }
    if (picked.length === 0) return; // cancelled

    const capacity = isMultiFile ? maxFiles - multiAssets.length : 1;
    const limited = picked.slice(0, Math.max(0, capacity));
    setDriveReused(null);
    setUploadState({ phase: 'uploading' });
    try {
      const { assets, errors } = await assetsFromDrive(
        spec.assetKind,
        drive.integrationUuid,
        limited
      );
      const failed =
        errors.length > 0 ? `Couldn't import ${errors.map((e) => e.name).join(', ')}.` : null;
      if (isMultiFile) {
        if (assets.length > 0) setMultiAssets((prev) => [...prev, ...assets].slice(0, maxFiles));
        setUploadState(failed ? { phase: 'error', message: failed } : { phase: 'idle' });
      } else if (assets[0]) {
        setUploadState({ phase: 'uploaded', asset: assets[0] });
      } else {
        setUploadState({ phase: 'error', message: failed ?? 'Import failed. Please try again.' });
      }
      // Already-in-library files were reused (existing asset kept usable). Offer a fresh-copy override.
      if (assets.some((a) => a.reused)) setDriveReused({ files: limited });
    } catch (err) {
      setUploadState({ phase: 'error', message: uploadErrorMessage(err) });
    }
  };

  // Confirm-or-override (workspace Drive import): re-import the picked files with force = fresh copies.
  const handleDriveForceImport = async () => {
    const spec = question.file;
    const reused = driveReused;
    if (!spec || !reused || !drive.integrationUuid) return;
    setDriveReused(null);
    setUploadState({ phase: 'uploading' });
    try {
      const { assets, errors } = await assetsFromDrive(
        spec.assetKind,
        drive.integrationUuid,
        reused.files,
        true // force a fresh copy, bypassing dedup
      );
      const failed =
        errors.length > 0 ? `Couldn't import ${errors.map((e) => e.name).join(', ')}.` : null;
      if (isMultiFile) {
        if (assets.length > 0) setMultiAssets((prev) => [...prev, ...assets].slice(0, maxFiles));
        setUploadState(failed ? { phase: 'error', message: failed } : { phase: 'idle' });
      } else if (assets[0]) {
        setUploadState({ phase: 'uploaded', asset: assets[0] });
      } else {
        setUploadState({ phase: 'error', message: failed ?? 'Import failed. Please try again.' });
      }
    } catch (err) {
      setUploadState({ phase: 'error', message: uploadErrorMessage(err) });
    }
  };

  // Render only once the storage-integration lookup has confirmed a connected Drive
  // (driveImportEnabled is false while loading and when none is connected) - never render
  // optimistically and then hide it.
  const driveImportButton = driveImportEnabled ? (
    <div className="ai-interaction__file-source">
      <span className="ai-interaction__file-source-or">or</span>
      <button
        type="button"
        className="ai-chat-btn ai-chat-btn--ghost"
        disabled={disabled || uploadState.phase === 'uploading'}
        onClick={() => void handleDriveImport()}
      >
        <img src="/integrations/google-drive/logo.png" alt="" className="ai-chat-btn__icon" />
        Import from Google Drive
      </button>
      <span className="ai-interaction__file-source-hint">from the workspace Drive</span>
      {driveReused ? (
        <span className="ai-interaction__file-source-hint">
          Already in your library - using the existing copy.{' '}
          <button
            type="button"
            className="ai-interaction__link-btn"
            disabled={disabled || uploadState.phase === 'uploading'}
            onClick={() => void handleDriveForceImport()}
          >
            Import a fresh copy
          </button>
        </span>
      ) : null}
    </div>
  ) : null;

  const handleFiles = async (files: FileList | File[]) => {
    const spec = question.file;
    if (!spec || disabled) return;
    const capacity = isMultiFile ? maxFiles - multiAssets.length : 1;
    const selected = Array.from(files).slice(0, Math.max(0, capacity));
    if (selected.length === 0) return;
    for (const file of selected) {
      // Accept by MIME, falling back to the filename extension when the browser sends a generic type
      // (application/octet-stream for a drag-dropped .csv/.xls). Mirrors the server's tolerant gate.
      const acceptMimes = spec.accept ?? [];
      const acceptExts = spec.acceptExtensions ?? [];
      const gated = acceptMimes.length > 0 || acceptExts.length > 0;
      const okByMime = acceptMimes.includes(file.type);
      const lowerName = file.name.toLowerCase();
      const okByExt = acceptExts.some((ext) => lowerName.endsWith(ext.toLowerCase()));
      if (gated && !okByMime && !okByExt) {
        setUploadState({ phase: 'error', message: 'That file type is not accepted here.' });
        return;
      }
      if (spec.maxBytes && file.size > spec.maxBytes) {
        setUploadState({ phase: 'error', message: 'The file is too large.' });
        return;
      }
    }
    setUploadState({ phase: 'uploading' });
    try {
      if (isMultiFile) {
        const uploaded: UploadedPromptAsset[] = [];
        for (const file of selected) {
          uploaded.push(await uploadPromptFile(spec.assetKind, file, { dealUuid: spec.dealUuid }));
        }
        setMultiAssets((prev) => [...prev, ...uploaded].slice(0, maxFiles));
        setUploadState({ phase: 'idle' });
      } else {
        const asset = await uploadPromptFile(spec.assetKind, selected[0]!, {
          dealUuid: spec.dealUuid,
        });
        setUploadState({ phase: 'uploaded', asset });
      }
    } catch (err) {
      // Upload errors keep the prompt pending - retrying never burns the ask.
      setUploadState({ phase: 'error', message: uploadErrorMessage(err) });
    }
  };

  return (
    <div className="ai-interaction__body">
      {question.link ? (
        <a
          className="ai-interaction__link-btn"
          href={question.link.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOauthError(null)}
        >
          {question.link.label} ↗
        </a>
      ) : null}
      {oauthError ? (
        <p className="ai-interaction__error" role="alert">
          {oauthError}
        </p>
      ) : null}

      {question.chips && question.chips.length > 0 ? (
        <div className="ai-interaction__chips">
          {question.chips.map((chip, i) => (
            <Pill key={`${chip.label}-${i}`} variant="neutral">
              {chip.label}
            </Pill>
          ))}
        </div>
      ) : null}

      {question.preview && !hasInlineDoc ? (
        <pre className="ai-interaction__preview-text ai-interaction__content-preview">
          {question.preview}
        </pre>
      ) : null}

      {optionRows.length > 0 ? (
        <div
          className={
            hasPreviews || hasInlineDoc
              ? 'ai-interaction__choices ai-interaction__choices--split'
              : 'ai-interaction__choices'
          }
        >
          <div
            className="ai-interaction__options"
            role="listbox"
            aria-label={question.prompt}
            tabIndex={0}
            onKeyDown={handleOptionsKeyDown}
          >
            {optionRows.map((option, index) => {
              const selected = isMulti
                ? checkedIds.has(option.id)
                : option.id === selectedId || (option.id === OTHER_OPTION_ID && otherSelected);
              return (
                <div
                  key={option.id}
                  role="presentation"
                  className="ai-interaction__option-row"
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <div className="ai-interaction__option-card">
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === activeIndex}
                      aria-checked={selected}
                      disabled={disabled}
                      className={[
                        'ai-interaction__option',
                        index === activeIndex ? 'ai-interaction__option--active' : '',
                        selected
                          ? isMulti
                            ? 'ai-interaction__option--selected ai-interaction__option--checked'
                            : 'ai-interaction__option--selected'
                          : '',
                        option.documentAssetUuid ? 'ai-interaction__option--has-preview' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onFocus={() => setActiveIndex(index)}
                      onClick={() => answerOption(option.id)}
                    >
                      <span className="ai-interaction__option-key" aria-hidden="true">
                        {selected ? '✓' : index + 1}
                      </span>
                      <span className="ai-interaction__option-main">
                        <span className="ai-interaction__option-label">
                          {option.label}
                          {option.id === recommendedId ? (
                            <span className="ai-interaction__option-recommended">
                              {' '}
                              (recommended)
                            </span>
                          ) : null}
                        </span>
                        {option.description ? (
                          <span className="ai-interaction__option-description">
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                    </button>
                    {option.documentAssetUuid ? (
                      <button
                        type="button"
                        className="ai-interaction__option-preview"
                        disabled={disabled}
                        onClick={() =>
                          setPreviewAsset({
                            uuid: option.documentAssetUuid!,
                            fileName: option.label,
                          })
                        }
                      >
                        Preview
                      </button>
                    ) : null}
                  </div>
                  {hasProfileContent(option.profile) ? (
                    <details className="ai-interaction__option-profile">
                      <summary className="ai-interaction__option-profile-trigger">
                        <span className="ai-interaction__option-profile-chevron" aria-hidden="true">
                          ▸
                        </span>
                        Details
                      </summary>
                      <div className="ai-interaction__option-profile-body">
                        {option.profile!.summary ? (
                          <p className="ai-interaction__option-profile-summary">
                            {option.profile!.summary}
                          </p>
                        ) : null}
                        {option.profile!.facts?.length ? (
                          <dl className="ai-interaction__option-profile-facts">
                            {option.profile!.facts.map((f, i) => (
                              <React.Fragment key={`f${i}`}>
                                <dt>{f.label}</dt>
                                <dd>{f.value}</dd>
                              </React.Fragment>
                            ))}
                          </dl>
                        ) : null}
                        {option.profile!.components?.length ? (
                          <div className="ai-interaction__option-profile-badges">
                            {option.profile!.components.map((c, i) => (
                              <span key={`c${i}`} className="ai-interaction__option-profile-badge">
                                {c.label}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {option.profile!.flowNotes?.length ? (
                          <ul className="ai-interaction__option-profile-notes">
                            {option.profile!.flowNotes.map((n, i) => (
                              <li key={`n${i}`}>{n}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </details>
                  ) : null}
                </div>
              );
            })}
          </div>
          {hasInlineDoc ? (
            <div className="ai-interaction__preview ai-interaction__preview--doc">
              <DocSectionView
                assetUuid={question.inlinePreviewAssetUuid ?? null}
                active
                heading={question.inlinePreviewHeading}
                fallbackText={question.preview}
                fileName={question.header}
                disabled={disabled}
                onViewFull={() =>
                  setPreviewAsset({
                    uuid: question.inlinePreviewAssetUuid!,
                    fileName: question.header,
                    scrollToHeading: question.inlinePreviewHeading,
                  })
                }
              />
            </div>
          ) : hasPreviews ? (
            <div className="ai-interaction__preview" aria-hidden="true">
              {previewImageUrl ? (
                <PreviewImage
                  key={previewOption?.previewAssetUuid}
                  src={previewImageUrl}
                  fallback={previewOption?.preview}
                />
              ) : previewOption?.preview ? (
                <pre className="ai-interaction__preview-text">{previewOption.preview}</pre>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {isMulti ? (
        <div className="ai-interaction__multi-actions">
          <span className="ai-interaction__multi-count">
            {checkedIds.size > 0
              ? `${checkedIds.size} selected${maxSelections ? ` / ${maxSelections}` : ''}`
              : minSelections > 0
                ? `Pick at least ${minSelections}`
                : 'Pick any that apply (optional)'}
          </span>
          <button
            type="button"
            className="ai-chat-btn ai-chat-btn--confirm"
            disabled={disabled || checkedIds.size < minSelections}
            onClick={confirmMulti}
          >
            {checkedIds.size === 0 && minSelections === 0 ? 'Skip' : 'Confirm'}
          </button>
        </div>
      ) : null}

      {question.kind === 'file' && isMultiFile ? (
        <div className="ai-interaction__file">
          {multiAssets.length > 0 ? (
            <ul className="ai-interaction__file-list">
              {multiAssets.map((asset, index) => (
                <li key={asset.uuid} className="ai-interaction__file-item">
                  <span className="ai-interaction__file-name">
                    {asset.originalFileName ?? `File ${index + 1}`}
                  </span>
                  <button
                    type="button"
                    className="ai-chat-btn ai-chat-btn--stop"
                    disabled={disabled}
                    aria-label={`Remove ${asset.originalFileName ?? `file ${index + 1}`}`}
                    onClick={() =>
                      setMultiAssets((prev) => prev.filter((a) => a.uuid !== asset.uuid))
                    }
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {multiAssets.length < maxFiles ? (
            <button
              type="button"
              className={
                dragOver
                  ? 'ai-interaction__dropzone ai-interaction__dropzone--over'
                  : 'ai-interaction__dropzone'
              }
              disabled={disabled || uploadState.phase === 'uploading'}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                void handleFiles(e.dataTransfer.files);
              }}
            >
              {uploadState.phase === 'uploading'
                ? 'Uploading…'
                : multiAssets.length === 0
                  ? `Drop files here, or click to browse (${minFiles}–${maxFiles})`
                  : `Add more files (${multiAssets.length}/${maxFiles})`}
            </button>
          ) : null}
          {multiAssets.length < maxFiles ? driveImportButton : null}
          <div className="ai-interaction__file-actions">
            <button
              type="button"
              className="ai-chat-btn ai-chat-btn--confirm"
              disabled={disabled || multiAssets.length < minFiles}
              onClick={() =>
                onAnswer({
                  kind: 'file',
                  assetUuid: multiAssets[0]?.uuid,
                  assetUuids: multiAssets.map((a) => a.uuid),
                })
              }
            >
              {multiAssets.length < minFiles
                ? `Add at least ${minFiles} file${minFiles === 1 ? '' : 's'}`
                : `Use ${multiAssets.length === 1 ? 'this file' : `these ${multiAssets.length} files`}`}
            </button>
          </div>
          {uploadState.phase === 'error' ? (
            <p className="ai-interaction__error" role="alert">
              {uploadState.message}
            </p>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={
              [...(question.file?.accept ?? []), ...(question.file?.acceptExtensions ?? [])].join(
                ','
              ) || undefined
            }
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      ) : null}

      {question.kind === 'file' && !isMultiFile ? (
        <div className="ai-interaction__file">
          {uploadState.phase === 'uploaded' ? (
            <div className="ai-interaction__file-preview">
              {previewUrl ? (
                <img
                  className="ai-interaction__file-image"
                  src={previewUrl}
                  alt={uploadState.asset.originalFileName ?? 'Uploaded image'}
                />
              ) : (
                <span>{uploadState.asset.originalFileName ?? 'Uploaded file'}</span>
              )}
              <div className="ai-interaction__file-actions">
                <button
                  type="button"
                  className="ai-chat-btn ai-chat-btn--confirm"
                  disabled={disabled}
                  onClick={() => onAnswer({ kind: 'file', assetUuid: uploadState.asset.uuid })}
                >
                  Use this file
                </button>
                <button
                  type="button"
                  className="ai-chat-btn"
                  disabled={disabled}
                  onClick={() => {
                    setUploadState({ phase: 'idle' });
                    fileInputRef.current?.click();
                  }}
                >
                  Replace
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={
                dragOver
                  ? 'ai-interaction__dropzone ai-interaction__dropzone--over'
                  : 'ai-interaction__dropzone'
              }
              disabled={disabled || uploadState.phase === 'uploading'}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                void handleFiles(e.dataTransfer.files);
              }}
            >
              {uploadState.phase === 'uploading'
                ? 'Uploading…'
                : 'Drop a file here, or click to browse'}
            </button>
          )}
          {uploadState.phase !== 'uploaded' ? driveImportButton : null}
          {uploadState.phase === 'error' ? (
            <p className="ai-interaction__error" role="alert">
              {uploadState.message}
            </p>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept={
              [...(question.file?.accept ?? []), ...(question.file?.acceptExtensions ?? [])].join(
                ','
              ) || undefined
            }
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      ) : null}

      {(question.kind === 'text' || (offerOther && otherSelected)) && question.kind !== 'file' ? (
        <form
          className="ai-interaction__text"
          onSubmit={(e) => {
            e.preventDefault();
            sendText();
          }}
        >
          {question.suggestions && question.suggestions.length > 0 ? (
            <div className="ai-interaction__suggestions">
              {question.suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="ai-interaction__suggestion"
                  disabled={disabled}
                  onClick={() => setText(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}
          <div className="ai-interaction__text-row">
            <textarea
              ref={textInputRef}
              className="ai-interaction__text-input"
              value={text}
              rows={1}
              disabled={disabled}
              autoFocus={autoFocus && question.kind === 'text'}
              placeholder={
                question.kind === 'text' ? 'Type or paste your answer…' : 'Or type your own answer…'
              }
              aria-label={question.prompt}
              onChange={(e) => {
                setText(e.target.value);
                autoGrowTextArea();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  focusComposer();
                } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  // Plain Enter inserts a newline (multi-line paste); Cmd/Ctrl+Enter sends.
                  e.preventDefault();
                  sendText();
                }
              }}
            />
            <button
              type="submit"
              className="ai-chat-btn ai-chat-btn--send"
              disabled={disabled || !text.trim()}
            >
              Send
            </button>
          </div>
          {question.kind === 'text' && question.optional ? (
            <button
              type="button"
              className="ai-interaction__skip"
              disabled={disabled}
              onClick={skipText}
            >
              Skip - leave this empty
            </button>
          ) : null}
        </form>
      ) : null}

      <FilePreviewDialog
        assetUuid={previewAsset?.uuid ?? null}
        fileName={previewAsset?.fileName}
        scrollToHeading={previewAsset?.scrollToHeading}
        open={previewAsset !== null}
        onOpenChange={(o) => {
          if (!o) setPreviewAsset(null);
        }}
      />
    </div>
  );
}

// ── Drawer ────────────────────────────────────────────────────────────────────

export function UserInteractionDrawer({ prompt, onRespond, disabled }: UserInteractionDrawerProps) {
  const isForm = prompt.kind === 'form' && (prompt.questions?.length ?? 0) > 0;

  return (
    <div className="ai-interaction" role="group" aria-label="Input required">
      <div className="ai-interaction__header">
        <p className="ai-interaction__prompt">{prompt.prompt}</p>
        {prompt.details ? <p className="ai-interaction__details">{prompt.details}</p> : null}
        {prompt.canGoBack ? (
          <button
            type="button"
            className="ai-interaction__back"
            disabled={disabled}
            onClick={() => onRespond(prompt.promptId, { kind: prompt.kind, action: 'back' })}
          >
            ← Back to previous question
          </button>
        ) : null}
      </div>
      {isForm ? (
        <FormBody
          promptId={prompt.promptId}
          questions={prompt.questions!}
          onRespond={onRespond}
          disabled={disabled}
        />
      ) : (
        <QuestionBody
          question={prompt}
          disabled={disabled}
          autoFocus
          onAnswer={(answer) => onRespond(prompt.promptId, { ...answer, kind: answer.kind })}
        />
      )}
    </div>
  );
}

// ── Tabbed multi-question (kind 'form') ───────────────────────────────────────

function FormBody({
  promptId,
  questions,
  onRespond,
  disabled,
}: {
  promptId: string;
  questions: UserPromptQuestion[];
  onRespond: (promptId: string, response: UserPromptResponse) => void;
  disabled?: boolean;
}) {
  const [answers, setAnswers] = React.useState<Record<string, QuestionAnswer>>({});
  const [activeTab, setActiveTab] = React.useState(questions[0]?.id ?? '');

  const submit = (finalAnswers: Record<string, QuestionAnswer>) =>
    onRespond(promptId, {
      kind: 'form',
      answers: questions.map((q) => ({ questionId: q.id, ...finalAnswers[q.id]! })),
    });

  // Answering a tab advances to the next still-unanswered one; once every tab is
  // answered the form submits itself - no Submit button (prev/next navigate).
  const recordAnswer = (questionId: string, answer: QuestionAnswer) => {
    const nextAnswers = { ...answers, [questionId]: answer };
    setAnswers(nextAnswers);
    if (questions.every((q) => nextAnswers[q.id])) {
      submit(nextAnswers);
      return;
    }
    const fromIdx = questions.findIndex((q) => q.id === questionId);
    for (let off = 1; off <= questions.length; off++) {
      const candidate = questions[(fromIdx + off) % questions.length];
      if (candidate && !nextAnswers[candidate.id]) {
        setActiveTab(candidate.id);
        break;
      }
    }
  };

  const active = questions.find((q) => q.id === activeTab) ?? questions[0]!;
  const activeIndex = Math.max(
    0,
    questions.findIndex((q) => q.id === active.id)
  );
  const answeredCount = questions.filter((q) => answers[q.id]).length;
  const goPrev = () => activeIndex > 0 && setActiveTab(questions[activeIndex - 1]!.id);
  const goNext = () =>
    activeIndex < questions.length - 1 && setActiveTab(questions[activeIndex + 1]!.id);

  return (
    <div className="ai-interaction__form">
      <Tabs
        ariaLabel="Questions"
        variant="plain"
        size="sm"
        value={active.id}
        onValueChange={setActiveTab}
        listClassName="ai-interaction__form-tablist"
        items={questions.map((q) => ({
          value: q.id,
          label: `${answers[q.id] ? '✓ ' : ''}${q.header ?? q.prompt}`,
        }))}
      />
      <div className="ai-interaction__form-question">
        <p className="ai-interaction__prompt">{active.prompt}</p>
        {active.details ? <p className="ai-interaction__details">{active.details}</p> : null}
        <QuestionBody
          key={active.id}
          question={active}
          disabled={disabled}
          initialAnswer={answers[active.id]}
          onAnswer={(answer) => recordAnswer(active.id, answer)}
        />
      </div>
      <div className="ai-interaction__form-nav">
        <button
          type="button"
          className="ai-interaction__nav-btn"
          aria-label="Previous section"
          disabled={disabled || activeIndex === 0}
          onClick={goPrev}
        >
          <ChevronLeftIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
        </button>
        <span className="ai-interaction__form-progress">
          {answeredCount} of {questions.length} confirmed
        </span>
        <button
          type="button"
          className="ai-interaction__nav-btn"
          aria-label="Next section"
          disabled={disabled || activeIndex >= questions.length - 1}
          onClick={goNext}
        >
          <ChevronRightIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
